/**
 * 이름 붙은 depth 밴드 레지스트리 — 뷰 order(z-order) 관리의 순수 메커니즘 (Phaser 비의존).
 *
 * 앱이 depth 상수 SSoT(예: depthLayers.ts)에서 밴드 스펙을 선언해 주입하면,
 * 본 클래스는 ① 밴드 범위 겹침을 생성 시점에 검증하고 ② `depthFor(band, index)`로
 * 밴드 내 index → 절대 depth를 계산한다. 상수 값 자체는 앱 소유 — 패키지는 메커니즘만 제공.
 *
 * 밴드 범위는 half-open `[base, base + step * capacity)` — 인접 밴드가 경계값을 공유해도
 * (예: modal [2000,3000) + toast base 3000) 겹침이 아니다.
 */
export interface DepthBandSpec {
  /** 밴드 시작 depth. */
  readonly base: number;
  /** index 1 증가당 depth 증분 (default 1). */
  readonly step?: number;
  /** 밴드가 수용하는 최대 슬롯 수 (default 100) — 범위 끝 = base + step * capacity. */
  readonly capacity?: number;
}

const DEFAULT_STEP = 1;
const DEFAULT_CAPACITY = 100;

export class DepthBands<TBand extends string = string> {
  private readonly bands: ReadonlyMap<TBand, Required<DepthBandSpec>>;

  constructor(bands: Readonly<Record<TBand, DepthBandSpec>>) {
    const resolved = new Map<TBand, Required<DepthBandSpec>>();
    for (const [name, spec] of Object.entries(bands) as [TBand, DepthBandSpec][]) {
      const step = spec.step ?? DEFAULT_STEP;
      const capacity = spec.capacity ?? DEFAULT_CAPACITY;
      if (step <= 0 || capacity <= 0) {
        throw new Error(`[DepthBands] band "${name}": step/capacity must be > 0`);
      }
      resolved.set(name, { base: spec.base, step, capacity });
    }
    // 겹침 검증 — base 오름차순으로 인접 쌍만 비교 (half-open 범위).
    const sorted = [...resolved.entries()].sort((a, b) => a[1].base - b[1].base);
    for (let i = 1; i < sorted.length; i++) {
      const [prevName, prev] = sorted[i - 1];
      const [curName, cur] = sorted[i];
      const prevEnd = prev.base + prev.step * prev.capacity;
      if (cur.base < prevEnd) {
        throw new Error(
          `[DepthBands] band "${curName}" (base ${cur.base}) overlaps "${prevName}" [${prev.base}, ${prevEnd})`,
        );
      }
    }
    this.bands = resolved;
  }

  /** 밴드 내 index(0부터)의 절대 depth — 미선언 밴드/범위 밖 index는 throw (silent 겹침 차단). */
  depthFor(band: TBand, index = 0): number {
    const spec = this.bands.get(band);
    if (!spec) throw new Error(`[DepthBands] unknown band "${band}"`);
    if (index < 0 || index >= spec.capacity) {
      throw new Error(
        `[DepthBands] band "${band}" index ${index} out of capacity ${spec.capacity}`,
      );
    }
    return spec.base + spec.step * index;
  }

  /** 밴드 시작 depth — 단일 객체(스택 불요)에 밴드 depth를 줄 때. */
  baseOf(band: TBand): number {
    return this.depthFor(band, 0);
  }
}
